import { useEffect, useCallback, useMemo, useContext } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { IntlContext } from '@/i18n';
import { getCitiesForOnboarding } from '@/modules/common/store/selectors';
import { loadDriver } from '@/modules/driver/actions/async/load-driver';
import { submitWelcomeStep } from '@/modules/onboarding/actions/submitWelcomeStep';
import {
  fsmCfg,
  fsmConfigPatchForFR,
  fsmConfigPatchForUK,
  fsmConfigPatchForUS,
} from '@/modules/onboarding/fsm-config';
import { TFsmEvent } from '@/modules/onboarding/fsm-types';
import {
  failedStepResult,
  isStepAccepted,
  splitStepErrors,
} from '@/modules/onboarding/model/stepErrors';
import { WelcomeForm } from '@/modules/onboarding/model/welcomeStep';
import { fsmSlice } from '@/modules/onboarding/store/fsm-slice';
import { createPartner } from '@/modules/partner/actions/async/createPartner';
import { loadPartner } from '@/modules/partner/actions/async/loadPartner';
import { IPartner } from '@/modules/partner/model';
import { getPartner } from '@/modules/partner/store/selectors';
import { addRole, loadUser } from '@/modules/user/actions';
import { getRoles, ROLES } from '@/modules/user/store/selectors';
import { REFERRAL_CODE_KEY } from '@/pages/Todo/hooks/useReferralCard';
import { IFormProps } from '@/pages/Wizard/view-types';
import { AppDispatch } from '@/root-store';
import { eventTracking } from '@/services/event-tracking';
import { queryParamsService } from '@/services/queryParams';
import { DEFAULT_LANGUAGE } from '@/services/userLanguage';
import { CountryCode, TCountryCodes } from '@/shared/models/country-code';
import { useForm } from '@/shared/view/Form';
import { ISubListItem } from '@src-common/view/components/ListInput';

import { messages } from './messages';
import { WelcomeView } from './view';

type Props = IFormProps<TFsmEvent>;

const mapPartnerToForm = (
  { firstName, lastName, middleName, cityId, email, referralCode, source }: IPartner,
  { externalReferralCode }: { externalReferralCode?: string },
): WelcomeForm => ({
  firstName,
  lastName,
  middleName,
  cityId,
  email,
  referralCode: externalReferralCode ?? referralCode,
  source,
});

const getCountryName = (intl: IntlShape, code: TCountryCodes) => {
  switch (code) {
    case CountryCode.GreatBritain:
      return intl.formatMessage(messages.countryUnitedKingdom);
    case CountryCode.Russia:
      return intl.formatMessage(messages.countryRussia);
    case CountryCode.France:
      return intl.formatMessage(messages.countryFrance);
    case CountryCode.ArabEmirates:
      return intl.formatMessage(messages.countryUnitedArabEmirates);
    case CountryCode.UnitedStates:
      return intl.formatMessage(messages.countryUsa);
    default:
      return intl.formatMessage(messages.countryOther);
  }
};

const Welcome = ({ onSwitchScreen }: Props) => {
  const { language } = useContext(IntlContext);
  const intl = useIntl();

  const dispatch = useDispatch() as AppDispatch;

  const partner = useSelector(getPartner);
  const userRoles = useSelector(getRoles);
  const cities = useSelector(getCitiesForOnboarding);

  const postForm = useCallback(
    async (formValues: WelcomeForm) => {
      if (!userRoles.includes(ROLES.PARTNER)) {
        const { errors } = await dispatch(createPartner()).unwrap();

        if (errors && !errors.partner_exists) {
          return splitStepErrors<WelcomeForm>(errors, {});
        }

        dispatch(addRole(ROLES.PARTNER));
      }

      const selectedCity = cities.find(city => city.id === formValues.cityId);
      const selectedCountryCode = selectedCity?.countryCode ?? null;

      const resultAction = await dispatch(submitWelcomeStep(formValues));

      if (submitWelcomeStep.fulfilled.match(resultAction)) {
        const { isSucceed, result = {} } = resultAction.payload;

        if (isSucceed && isStepAccepted(result)) {
          await dispatch(loadUser()).unwrap();

          dispatch(loadDriver());
          dispatch(loadPartner());

          eventTracking.track({
            event: 'Onboarding - Contacts - Success',
            city: selectedCity?.name,
            country: selectedCountryCode,
          });

          switch (selectedCountryCode) {
            case 'GB':
              dispatch(fsmSlice.actions.patchTransitions(fsmConfigPatchForUK));

              break;
            case 'FR':
              dispatch(fsmSlice.actions.patchTransitions(fsmConfigPatchForFR));

              break;
            case 'US':
              dispatch(fsmSlice.actions.patchTransitions(fsmConfigPatchForUS));

              break;
            default:
              dispatch(fsmSlice.actions.patchTransitions(fsmCfg));

              break;
          }

          onSwitchScreen('NEXT');
        }

        return result;
      }

      return failedStepResult(resultAction.error);
    },
    [dispatch, onSwitchScreen, userRoles, cities],
  );

  const {
    handleSubmit,
    form: {
      control,
      formState: { isSubmitting },
    },
  } = useForm({
    defaultValues: mapPartnerToForm(partner, {
      externalReferralCode: queryParamsService.getSingleByName(REFERRAL_CODE_KEY),
    }),
    onSubmit: postForm,
  });

  useEffect(() => {
    eventTracking.track({
      event: 'Onboarding - Start',
    });
  }, []);

  const cityOptions = useMemo((): ISubListItem[] => {
    const regions: Map<string, ISubListItem> = new Map();

    cities.forEach(city => {
      const countryName = getCountryName(intl, city.countryCode);
      let region = regions.get(countryName);

      if (!region) {
        region = {
          title: countryName,
          items: [],
        };

        regions.set(countryName, region);
      }

      region.items.push({
        title: city.localizedName[language] || city.localizedName[DEFAULT_LANGUAGE],
        value: city.id,
      });
    });

    return [...regions.values()];
  }, [cities, intl, language]);

  return (
    <WelcomeView
      control={control}
      onSubmit={handleSubmit}
      cities={cityOptions}
      isSubmitting={isSubmitting}
    />
  );
};

export { Welcome };
